import React, { useEffect, useState, useRef } from 'react';
import './custom-swipeable-drawer.scss';

interface CustomSwipeableDrawerProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  height?: string;
  maxHeight?: string;
  backdrop?: boolean;
  showHandle?: boolean;
}

export const CustomSwipeableDrawer: React.FC<CustomSwipeableDrawerProps> = ({
  open,
  onClose,
  children,
  height,
  maxHeight = '50%',
  backdrop = true,
  showHandle = true,
}) => {
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [startY, setStartY] = useState<number>(0);
  const [translateY, setTranslateY] = useState<number>(100); // Start closed
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isClosing, setIsClosing] = useState<boolean>(false);
  const drawerRef = useRef<HTMLDivElement | null>(null);

  const closeDrawer = () => {
    setIsClosing(true);
    setTranslateY(100);
    setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (isClosing) return;
    setIsDragging(true);
    setStartY(e.touches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || isClosing) return;

    e.preventDefault();
    const touch = e.touches[0];
    const deltaY = touch.clientY - startY;

    if (deltaY > 0) {
      const percentage = (deltaY / window.innerHeight) * 100;
      setTranslateY(Math.min(percentage, 100));
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging || isClosing) return;

    setIsDragging(false);

    if (translateY > 30) {
      closeDrawer();
    } else {
      setTranslateY(0);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (isClosing) return;
    setIsDragging(true);
    setStartY(e.clientY);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || isClosing) return;

    e.preventDefault();
    const deltaY = e.clientY - startY;

    if (deltaY > 0) {
      const percentage = (deltaY / window.innerHeight) * 100;
      setTranslateY(Math.min(percentage, 100));
    }
  };

  const handleMouseUp = () => {
    if (!isDragging || isClosing) return;

    setIsDragging(false);

    if (translateY > 30) {
      closeDrawer();
    } else {
      setTranslateY(0);
    }
  };

  const handleBackdropClick = () => {
    if (isClosing) return;
    closeDrawer();
  };

  useEffect(() => {
    if (open && !isVisible && !isClosing) {
      setIsVisible(true);
      setTimeout(() => {
        setTranslateY(0);
      }, 10);
    } else if (!open && isVisible && !isClosing) {
      closeDrawer();
    }
  }, [open, isVisible, isClosing]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, startY, translateY, isClosing]);

  // Don't render if not visible
  if (!isVisible) return null;

  return (
    <>
      {backdrop && (
        <div
          className="custom-swipeable-drawer-backdrop"
          style={{
            opacity: open && !isClosing ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
          }}
          onClick={handleBackdropClick}
        />
      )}

      <div
        className="custom-swipeable-drawer-body"
        ref={drawerRef}
        style={{
          ...(height ? { height: height } : {}),
          maxHeight: maxHeight,
          transform: `translateY(${translateY}%)`,
          transition: isDragging ? 'none' : 'transform 0.3s ease-in-out',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
      >
        {showHandle && <div className="custom-swipeable-drawer-body-handle" />}

        {/* Content */}
        <div className="custom-swipeable-drawer-body-content">{children}</div>
      </div>
    </>
  );
};
